<template>
  <component
    :is="forList ? 'a' : 'div'"
    class="ss__user__card__wrapper"
    :href="href"
    :target="target">
    <div :class="[
      'ss__user__card',
      {'ss__user__card--list': forList}]">
      <div class="ss__user__card__header">
        <a :href="`/u/${user.slug}`" target="_blank">
          <img
            class="ss__user__avatar"
            :src="$formatImageSrc(user.avatar, { w: 100, h: 100 })"
            width="40" height="40"
            @error="onError"
          />
        </a>
        <template v-if="!isSelf">
          <button
            :class="[
              'ss__user__card__btn',
              {'ss__user__card__btn--followed': followed}
            ]"
            @click.prevent="updateFollowStatus">
            <template v-if="!followed">
              <i class="iconfont-add--stroke"/>
              <span>关注</span>
            </template>
            <template v-else>
              <i class="iconfont-success--stroke"/>
            </template>
          </button>
        </template>
      </div>
      <div class="ss__user__card__center">
        <div class="ss__user__card__nickname__wrapper">
          <div class="ss__user__card__nickname">{{ user.nickname }}</div>
          <SsUserIdentifier
            v-if="forList && role"
            :identifier="role"
            popoverPlacement="top"
          />
          <SsUserIdentifier
            v-if="forList && projectVerification"
            class="comp__SsUserIdentifier--projectVerification"
            :identifier="projectVerification"
            popoverPlacement="top"
          />
        </div>
        <div v-if="!forList && (role || projectVerification)" class="ss__user__card__role__wrapper">
          <div v-if="role" class="ss__user__card__role__item">
            <SsUserIdentifier
              class="role__icon"
              :identifier="role"
              :popoverDisable="true"
            />
            <span class="role__name">{{ role.name }}</span>
          </div>
          <div v-if="projectVerification" class="ss__user__card__role__item">
            <SsUserIdentifier
              class="role__icon comp__SsUserIdentifier--projectVerification"
              :identifier="projectVerification"
              :popoverDisable="true"
            />
            <span class="role__name">{{ projectVerification.name }}</span>
          </div>
        </div>
      </div>
      <div
        v-if="user.bio"
        :class="[
          'ss__user__card__intro',
          forList ? 'text_ellipsis' : 'text_ellipsis2']">
          {{ user.bio }}
        </div>
      <div v-if="followedCount || forList" class="ss__user__card__footer">
        <span v-if="followedCount">{{ followedCount }} 人正在关注</span>
        <span v-if="forList && articleCount">{{ articleCount }} 篇文章</span>
      </div>
    </div>
  </component>
</template>

<script>
import debounce from 'lodash/debounce'
import SsUserIdentifier from '../SsUserIdentifier'

export default {
  name: 'SsUserCard',

  components: {
    SsUserIdentifier
  },

  props: {
    user: {
      type: Object,
      default: () => ({
        avatar: 'https://cdn-static.sspai.com/ui/otter_avatar_placeholder_240511.png',
        nickname: ''
      })
    },
    isSelf: {
      type: Boolean,
      default: false
    },
    isLogin: {
      type: Boolean,
      default: false
    },
    forList: {
      type: Boolean,
      default: false
    },
    followedCountKey: {
      type: String,
      default: 'followed_count'
    },
    articleCountKey: {
      type: String,
      default: 'released_article_count'
    },
    identifierKey: {
      type: String,
      default: 'user_flags'
    },
    projectVerificationKey: {
      type: String,
      default: 'user_id_verify_flags'
    },
    href: String,
    target: String
  },

  data () {
    return {
    }
  },

  computed: {
    followed () {
      return this.user.followed
    },
    followedCount () {
      return this.user[this.followedCountKey]
    },
    articleCount () {
      return this.user[this.articleCountKey]
    },
    role () {
      if (!this.user) return
      const roles = this.user[this.identifierKey]
      if (!roles || !Array.isArray(roles) || !roles[0]) return
      return roles[0]
    },
    projectVerification () {
      if (!this.user) return
      const flags = this.user[this.projectVerificationKey]
      if (!flags || !Array.isArray(flags) || !flags[0]) return
      return flags[0]
    }
  },

  methods: {
    onError (e) {
      e.target.src = 'https://cdn-static.sspai.com/ui/otter_avatar_placeholder_240511.png'
    },
    updateFollowStatus: debounce(async function () {
      if (!this.isLogin) {
        this.$toLogin(this)
        return
      }
      const flag = this.followed ? 2 : 1
      try {
        const res = await this.$http({
          method: 'post',
          url: '/user/follow',
          data: {
            slug: this.user.slug,
            flag
          }
        })
        if (!res) throw new Error('请求超时或取消！')
        if (res.error) throw new Error(res)
        this.$message({
          message: flag === 2 ? '已取消关注' : '关注成功',
          type: 'success'
        })
        this.user.followed = !this.user.followed
      } catch (error) {
        console.log(error)
      }
    }, 200)
  }
}
</script>

<style lang="stylus" scoped>
@import '~@/assets/styles/media.styl'
.ss__user__card__wrapper {
  display: block
  width: var(--width-SsUserCard, 264px)
  border-radius: var(--radius-SsUserCard, 16px)
  box-shadow: var(--shadow-SsUserCard, var(--shadow-level-2))
  padding: var(--padding-SsUserCard, 16px)
  background: var(--color-bg-SsUserCard, var(--B0-t-overlay))
  box-sizing: border-box
  transition: .2s

  .ss__user__card__header {
    display: flex
    justify-content: space-between
    margin-bottom: 8px
  }

  .ss__user__card__center {
    margin-bottom: 12px
  }

  .ss__user__card__intro {
    margin-bottom: 12px
  }

  .ss__user__card__footer {
    color: var(--color-SsUserCardFooter, var(--B6-t))
    font-size: var(--text-SsUserCardFooter, 13px)
    line-height: var(--leading-SsUserCardFooter, 18px)
    span {
      margin-right: 16px
    }
  }

  .ss__user__avatar {
    width: var(--size-SsUserCardAvatar, 40px)
    height: var(--size-SsUserCardAvatar, 40px)
    border: 0.5px solid rgba(0, 0, 0, 0.05)
    border-radius: 50%
    object-fit: contain
    box-sizing: border-box
    background: #fff;
    background-clip: content-box;
  }

  .ss__user__card__nickname__wrapper {
    margin-bottom: 2px;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .ss__user__card__btn {
    width: var(--width-SsUserCardBtn, 60px)
    height: var(--height-SsUserCardBtn, 26px)
    border-radius: var(--radius-SsUserCardBtn, 6px)
    display: flex
    justify-content: center
    align-items: center
    font-size: var(--text-SsUserCardBtn, 13px)
    font-weight: var(--weight-SsUserCardBtn, 500)
    position relative
    &::after {
      content: ''
      background: var(--color-SsUserCardBtnHoverMask, rgba(255, 255, 255, 0.2))
      position: absolute
      width: 100%
      height: 100%
      border-radius: var(--radius-SsUserCardBtn, 6px)
      top: 0
      left: 0
      opacity: 0
      transition: opacity .2s
      z-index: 0
    }
    i {
      font-size: var(--size-SsUserCardBtnIcon, 16px)
      font-weight: normal
    }
    span {
      z-index: 2
    }
    &:not(.ss__user__card__btn--followed) {
      background: var(--color-SsUserCardBtnBg, var(--brand))
      color: var(--color-SsUserCardBtn, #ffffff)
      i {
        margin-right: var(--spacing-SsUserCardBtnIcon, 2px)
      }
    }
    &.ss__user__card__btn--followed {
      background: var(--color-SsUserCardBtnBg--followed, var(--B1-t))
      color: var(--color-SsUserCardBtn, var(--green-t))
    }
    &:hover::after {
      opacity: 1
    }
  }

  .ss__user__card__nickname {
    color: var(--color-SsUserCardNn, var(--B8-t))
    font-size: var(--text-SsUserCardNn, 13px)
    line-height: var(--leading-SsUserCardNn, 18px)
    font-weight: var(--weight-SsUserCardNn, 500)
  }

  .ss__user__card__role__wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }

  .comp__SsUserIdentifier--projectVerification {
    >>> .ss__user__identifier__icon {
      border-radius: 2px;
      border: 1px solid var(--color-imageBorder);
    }
  }

  .ss__user__card__role__item {
    display: flex;
    align-items: center;
    gap: 4px;
    .role__name {
      color: var(--color-SsUserCardRole, var(--B8-t))
      font-size: var(--text-SsUserCardRole, 13px)
      line-height: var(--leading-SsUserCardRole, 18px)
    }
  }

  .ss__user__card__intro {
    color: var(--color-SsUserCardIntro, var(--B7-t))
    font-size: var(--text-SsUserCardIntro, 13px)
    line-height: var(--leading-SsUserCardIntro, 18px)
  }

  .ss__user__card > div:last-child {
    margin-bottom: 0px
  }

  &:is(a):hover {
    background: var(--color-bg-SsUserCard-hover, var(--B2-t))
  }

  .ss__user__card--list {
    height: 100%
    display: flex
    flex-direction: column
    .ss__user__card__center {
      margin-bottom: 0px
    }
    .ss__user__card__intro {
      margin-bottom: 16px
      @media (min-width: $screen-lg) {
        margin-bottom: 24px
      }
    }
    .ss__user__avatar {
      width: var(--size-SsUserCardAvatar, 48px)
      height: var(--size-SsUserCardAvatar, 48px)
    }
    .ss__user__card__btn {
      &:not(.ss__user__card__btn--followed) {
        background: var(--color-SsUserCardBtnBg, var(--B1-t))
        color: var(--color-SsUserCardBtn, var(--brand))
      }
    }
    .ss__user__card__nickname__wrapper {
      display: flex
      align-items: center
    }
    .ss__user__card__nickname {
      font-size: var(--text-SsUserCardNn, 15px)
      line-height: var(--leading-SsUserCardNn, 21px)
    }
    .ss__user__card__intro {
      color: var(--color-SsUserCardIntro, var(--B7-t))
    }
    .ss__user__card__footer {
      margin-top: auto
    }
  }
}
</style>
